import { format, startOfMonth, subMonths } from 'date-fns';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useState } from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    H6Emphasized,
    Spinner,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { ConfirmModal } from '../../common/components/ConfirmModal';
import { Button } from '../../common/components/GroveButtons';
import { MutationSuccessToastOptions } from '../../common/components/Toasts';
import { centsToFormattedDollars } from '../../common/utils/formatting';
import {
    type InterestNotificationTotalsResponseDto,
    type InterestPostedDetailsBySubtype,
    type InterestTotalsResponseDto,
    useGetInterestTotals,
} from '../hooks';
import { useGetInterestNotificationTotals } from '../hooks/useInterestNotificationTotals';
import { useTriggerInterestNotifications } from '../hooks/useTriggerInterestNotifications';

export const BankOpsInterestPanel = () => {
    // Defaults to prior month; interest posted at EOM so current month almost always empty
    const defaultDate = startOfMonth(subMonths(new Date(), 1));
    const [interestDate, setInterestDate] = useState<Date>(defaultDate);

    const toast = useToastController();

    const { data: interestTotals, isLoading: isLoadingInterestTotals } =
        useGetInterestTotals(
            {
                date: format(interestDate, 'yyyy-MM-dd'),
            },
            {
                enabled: interestDate != null,
            },
        );

    const {
        data: interestNotificationTotals,
        isLoading: isLoadingInterestNotificationTotals,
    } = useGetInterestNotificationTotals(
        {
            date: format(interestDate, 'yyyy-MM-dd'),
        },
        {
            enabled: interestDate != null,
        },
    );

    const monthDisplay = interestDate.toLocaleString('default', {
        month: 'long',
        year: 'numeric',
    });

    const {
        mutateAsync: triggerInterestNotifications,
        isPending: triggerInterestNotificationsPending,
    } = useTriggerInterestNotifications();

    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const handleTriggerInterestNoticationsSubmit = async () => {
        setIsConfirmModalOpen(false);
        const result = await triggerInterestNotifications({
            body: {
                yearMonth: format(interestDate, 'yyyy-MM-dd'),
            },
        });
        toast.show(
            `Successfully sent ${result.notificationsSent} interest push notifications for ${monthDisplay}`,
            MutationSuccessToastOptions,
        );
    };

    const handleDateChange = (value: Date | null): void => {
        if (value) {
            setInterestDate(value);
        }
    };

    if (
        isLoadingInterestTotals ||
        interestTotals == null ||
        isLoadingInterestNotificationTotals
    ) {
        return <Spinner aria-label="Loading interest totals" />;
    }

    const totalInterestPayout = interestTotals.interestPayout.total ?? 0;
    const hasPositiveInterest = totalInterestPayout > 0;

    return (
        <YStack gap={24} width="100%">
            {/* Interest Totals Section */}
            <YStack gap={16}>
                <XStack
                    justifyContent="space-between"
                    width="100%"
                    alignItems="center"
                    role="banner"
                >
                    <H6Emphasized>
                        Interest Totals - {monthDisplay}
                    </H6Emphasized>
                    <XStack alignItems="center" gap={8}>
                        <BodyMediumRegular>Month:</BodyMediumRegular>
                        <Calendar
                            value={interestDate}
                            onChange={(e) => {
                                handleDateChange(e.value as Date);
                            }}
                            showIcon
                            dateFormat="mm/yy"
                            view="month"
                            yearNavigator
                            yearRange="2020:2030"
                            aria-label="Select month for interest totals"
                            maxDate={startOfMonth(new Date())}
                        />
                    </XStack>
                </XStack>
                <InterestTotalsTable interestTotals={interestTotals ?? null} />
            </YStack>

            {/* Interest Notifications Section */}
            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={() => {
                    setIsConfirmModalOpen(false);
                }}
                onConfirm={handleTriggerInterestNoticationsSubmit}
            />
            <YStack
                gap={12}
                backgroundColor="$gray100"
                padding={16}
                borderRadius={8}
                role="region"
                aria-label="Interest Notifications Control"
            >
                <H6Emphasized>Monthly Interest Notifications</H6Emphasized>
                <BodyMediumRegular>
                    Send push notifications to all users summarizing their
                    earned interest for {monthDisplay}.
                </BodyMediumRegular>
                <XStack gap={16} alignItems="center">
                    <Button
                        variant={hasPositiveInterest ? 'primary' : 'secondary'}
                        size="small"
                        disabled={
                            triggerInterestNotificationsPending ||
                            !hasPositiveInterest
                        }
                        onPress={(_) => {
                            setIsConfirmModalOpen(true);
                        }}
                        aria-label={
                            triggerInterestNotificationsPending
                                ? 'Processing interest notifications'
                                : 'Send interest notifications to all users'
                        }
                        aria-busy={triggerInterestNotificationsPending}
                        aria-disabled={
                            triggerInterestNotificationsPending ||
                            !hasPositiveInterest
                        }
                    >
                        {triggerInterestNotificationsPending
                            ? 'Sending Notifications...'
                            : 'Send Interest Notifications'}
                    </Button>
                    {triggerInterestNotificationsPending && (
                        <BodyMediumRegular color="$gray800">
                            This may take a few minutes...
                        </BodyMediumRegular>
                    )}
                </XStack>
                <InterestNotificationTotalsTable
                    interestNotificationTotals={
                        interestNotificationTotals ?? null
                    }
                />
            </YStack>
        </YStack>
    );
};

const InterestTotalsTable = ({
    interestTotals,
}: {
    interestTotals: InterestTotalsResponseDto | null;
}) => {
    if (interestTotals == null) {
        return null;
    }

    const interestTotalsData = [interestTotals];

    return (
        <YStack gap={8}>
            <DataTable
                value={interestTotalsData}
                size="small"
                tableStyle={{ width: '100%' }}
                showGridlines
            >
                <Column
                    field="month"
                    header="Month"
                    body={InterestTotalsMonthRenderer}
                />
                <Column
                    field="interestPayout"
                    header="Interest payout"
                    body={InterestTotalsInterestPayoutDataRenderer}
                />
                <Column
                    field="interestPayoutReversal"
                    header="Interest payout reversal"
                    body={InterestTotalsInterestPayoutReversalDataRenderer}
                />
                <Column
                    field="interestCorrection"
                    header="Interest correction"
                    body={InterestTotalsInterestCorrectionDataRenderer}
                />
                <Column
                    field="interestCorrectionReversal"
                    header="Interest correction reversal"
                    body={InterestTotalsInterestCorrectionReversalDataRenderer}
                />
            </DataTable>
        </YStack>
    );
};

const InterestTotalsMonthRenderer = ({
    monthStart,
    monthEnd,
}: InterestTotalsResponseDto) => {
    return (
        <YStack>
            <BodyMediumRegular>Start: {monthStart}</BodyMediumRegular>
            <BodyMediumRegular>End: {monthEnd}</BodyMediumRegular>
        </YStack>
    );
};

const InterestTotalsInterestPayoutDataRenderer = (
    interestPostedDetailsBySubtype: InterestTotalsResponseDto,
) => {
    return InterestTotalsDataSubtypeRenderer(
        interestPostedDetailsBySubtype.interestPayout,
    );
};

const InterestTotalsInterestPayoutReversalDataRenderer = (
    interestPostedDetailsBySubtype: InterestTotalsResponseDto,
) => {
    return InterestTotalsDataSubtypeRenderer(
        interestPostedDetailsBySubtype.interestPayoutReversal,
    );
};

const InterestTotalsInterestCorrectionDataRenderer = (
    interestPostedDetailsBySubtype: InterestTotalsResponseDto,
) => {
    return InterestTotalsDataSubtypeRenderer(
        interestPostedDetailsBySubtype.interestCorrection,
    );
};

const InterestTotalsInterestCorrectionReversalDataRenderer = (
    interestPostedDetailsBySubtype: InterestTotalsResponseDto,
) => {
    return InterestTotalsDataSubtypeRenderer(
        interestPostedDetailsBySubtype.interestCorrectionReversal,
    );
};

const InterestTotalsDataSubtypeRenderer = ({
    total,
    num,
    numDebits,
}: InterestPostedDetailsBySubtype) => {
    return (
        <YStack>
            <BodyMediumRegular>
                Amount: {centsToFormattedDollars(total)}
            </BodyMediumRegular>
            <BodyMediumRegular>Num transactions: {num}</BodyMediumRegular>
            {numDebits > 0 && (
                <BodyMediumEmphasized>
                    Num debits: {numDebits}
                </BodyMediumEmphasized>
            )}
        </YStack>
    );
};

const InterestNotificationTotalsTable = ({
    interestNotificationTotals,
}: {
    interestNotificationTotals: InterestNotificationTotalsResponseDto | null;
}) => {
    if (interestNotificationTotals == null) {
        return null;
    }

    const interestNotificationTotalsData = [interestNotificationTotals];

    return (
        <YStack gap={8}>
            <DataTable
                value={interestNotificationTotalsData}
                size="small"
                tableStyle={{ width: '100%' }}
                showGridlines
            >
                <Column
                    field="month"
                    header="Month"
                    body={({
                        monthStart,
                        monthEnd,
                    }: InterestNotificationTotalsResponseDto) => {
                        return (
                            <YStack>
                                <BodyMediumRegular>
                                    Start: {monthStart}
                                </BodyMediumRegular>
                                <BodyMediumRegular>
                                    End: {monthEnd}
                                </BodyMediumRegular>
                            </YStack>
                        );
                    }}
                />
                <Column
                    field="interestTransactionsLogged"
                    header="Interest transactions logged"
                    body={({
                        aggregateInterestPaid,
                    }: InterestNotificationTotalsResponseDto) => {
                        return (
                            <YStack>
                                <BodyMediumRegular>
                                    Amount:{' '}
                                    {centsToFormattedDollars(
                                        aggregateInterestPaid.aggregateInterest,
                                    )}
                                </BodyMediumRegular>
                                <BodyMediumRegular>
                                    Num transactions:{' '}
                                    {
                                        aggregateInterestPaid.numInterestTransactions
                                    }
                                </BodyMediumRegular>
                            </YStack>
                        );
                    }}
                />
                <Column
                    field="aggregateInterestNotificationStatistics"
                    header="Interest notification statistics"
                    body={({
                        aggregateInterestNotificationStatistics,
                    }: InterestNotificationTotalsResponseDto) => {
                        return (
                            <YStack>
                                <BodyMediumRegular>
                                    Users with interest:{' '}
                                    {
                                        aggregateInterestNotificationStatistics.numUsersWithInterest
                                    }
                                </BodyMediumRegular>
                                <BodyMediumRegular>
                                    Users with interest marked sent
                                    notification:{' '}
                                    {
                                        aggregateInterestNotificationStatistics.numUsersWithInterestMarkedSentNotification
                                    }
                                </BodyMediumRegular>
                                <BodyMediumRegular>
                                    Users with interest and push device enabled:{' '}
                                    {
                                        aggregateInterestNotificationStatistics.numUsersWithInterestAndPushDeviceEnabled
                                    }
                                </BodyMediumRegular>
                                <BodyMediumRegular>
                                    Users with negative interest:{' '}
                                    {
                                        aggregateInterestNotificationStatistics.numUsersSkipNegativeInterest
                                    }
                                </BodyMediumRegular>
                                <BodyMediumRegular>
                                    Users with all closed accounts:{' '}
                                    {
                                        aggregateInterestNotificationStatistics.numUsersSkipAllClosedAccounts
                                    }
                                </BodyMediumRegular>
                            </YStack>
                        );
                    }}
                />
            </DataTable>
        </YStack>
    );
};
